import { useCallback, useEffect, useRef, useState } from "react";
import API_BASE_URL from "../config/api";
import SearchableDropdown from "./SearchableDropdown";
import "./TopluAnaliz.css";

/* ─── helpers ─── */
function pctColor(v) {
  if (v == null) return "#94a3b8";
  const h = (Math.max(0, Math.min(100, v)) / 100) * 120;
  return `hsl(${h},65%,48%)`;
}

function PctBadge({ value }) {
  if (value == null) return <span className="ta-badge ta-badge-gray">—</span>;
  const cls = value < 45 ? "ta-badge-red"
            : value < 60 ? "ta-badge-orange"
            : value < 75 ? "ta-badge-yellow"
            : value < 90 ? "ta-badge-teal"
            : "ta-badge-green";
  return <span className={`ta-badge ${cls}`}>{value.toFixed(1)}</span>;
}

function categoryFromPercent(p) {
  const x = Number(p ?? 0);
  if (x >= 90) return { key: 4, label: 'Mükemmel' };
  if (x >= 75) return { key: 3, label: 'Yeterli' };
  if (x >= 60) return { key: 2, label: 'Ortalama Üstü' };
  if (x >= 45) return { key: 1, label: 'Geliştirmeye Açık' };
  return { key: 0, label: 'İyileştirilmeli' };
}

const SITE_LABELS = {
  // Türkçe ders çalışma siteleri
  wikipedia_tr:  "Vikipedi (TR)",
  acikders:      "Açık Ders",
  udemy_tr:      "Udemy (Türkçe)",
  khanacademy:   "Khan Academy TR",
  // Yabancı ders çalışma siteleri
  wikipedia:     "Vikipedi",
  mit_ocw:       "MIT OpenCourseWare",
  coursera:      "Coursera",
  udemy:         "Udemy",
  edx:           "edX",
  brilliant:     "Brilliant",
  openstax:      "OpenStax",
  libretexts:    "LibreTexts",
  wikiversity:   "Wikiversity",
  geeksforgeeks: "GeeksforGeeks",
  w3schools:     "W3Schools",
  tutorialspoint:"Tutorialspoint",
  programiz:     "Programiz",
  freecodecamp:  "freeCodeCamp",
  stackoverflow: "Stack Overflow",
  youtube:       "YouTube",
  google:        "Google",
};

const SITE_DOMAINS = {
  wikipedia_tr:  "tr.wikipedia.org",
  acikders:      "acikders.org.tr",
  udemy_tr:      "udemy.com",
  khanacademy:   "tr.khanacademy.org",
  wikipedia:     "en.wikipedia.org",
  mit_ocw:       "ocw.mit.edu",
  coursera:      "coursera.org",
  udemy:         "udemy.com",
  edx:           "edx.org",
  brilliant:     "brilliant.org",
  openstax:      "openstax.org",
  libretexts:    "libretexts.org",
  wikiversity:   "en.wikiversity.org",
  geeksforgeeks: "geeksforgeeks.org",
  w3schools:     "w3schools.com",
  tutorialspoint:"tutorialspoint.com",
  programiz:     "programiz.com",
  freecodecamp:  "freecodecamp.org",
  stackoverflow: "stackoverflow.com",
};

function buildWebUrl(site_key, q) {
  const eq = encodeURIComponent(q);
  // Wikipedia ve Wikiversity güvenilir arama URL'sine sahip
  if (site_key === "wikipedia_tr")
    return `https://tr.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikipedia")
    return `https://en.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikiversity")
    return `https://en.wikiversity.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "udemy_tr")
    return `https://www.udemy.com/courses/search/?q=${eq}&lang=tr`;
  if (site_key === "udemy")
    return `https://www.udemy.com/courses/search/?q=${eq}`;
  if (site_key === "coursera")
    return `https://www.coursera.org/search?query=${eq}`;
  if (site_key === "stackoverflow")
    return `https://stackoverflow.com/search?q=${eq}`;
  if (site_key === "youtube")
    return `https://www.youtube.com/results?search_query=${eq}`;
  if (site_key === "khanacademy")
    return `https://tr.khanacademy.org/search?page_search_query=${eq}`;
  if (site_key === "mit_ocw")
    return `https://ocw.mit.edu/search/?q=${eq}`;
  if (site_key === "freecodecamp")
    return `https://www.freecodecamp.org/news/search/?query=${eq}`;
  if (site_key === "geeksforgeeks")
    return `https://www.geeksforgeeks.org/search/?q=${eq}`;
  // Diğer tüm siteler için Google site araması — 404 vermez
  const domain = SITE_DOMAINS[site_key];
  if (domain) return `https://www.google.com/search?q=site:${domain}+${eq}`;
  return `https://www.google.com/search?q=${eq}`;
}

const TYPE_ICONS = { tutorial: "📖", article: "📄", documentation: "📋", interactive: "🎮" };
function typeIcon(t) { return TYPE_ICONS[t] || "🔗"; }

/* ─── OutcomeRow: kompakt satır, linkler direkt görünür ─── */
function OutcomeRow({ oc, analysis }) {
  const [ytMap, setYtMap] = useState({});
  const [ytLoading, setYtLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!analysis?.videos?.length || fetchedRef.current) return;
    fetchedRef.current = true;
    const queries = [...new Set(analysis.videos.map(v => v.search_query).filter(Boolean))];
    if (!queries.length) return;
    setYtLoading(true);
    Promise.all(
      queries.map(q =>
        fetch(`${API_BASE_URL}/api/youtube/search?q=${encodeURIComponent(q)}&max=1`, { credentials: "include" })
          .then(r => r.json())
          .then(d => ({ q, video: (d.videos || [])[0] || null }))
          .catch(() => ({ q, video: null }))
      )
    ).then(results => {
      const map = {};
      results.forEach(({ q, video }) => { map[q] = video; });
      setYtMap(map);
      setYtLoading(false);
    });
  }, [analysis]);

  const videos = analysis?.videos || [];
  const webRes = analysis?.web_resources || [];
  const hasLinks = videos.length > 0 || webRes.length > 0;

  return (
    <div className="ta-oc-row">
      <div className="ta-oc-header">
        <span className="ta-oc-code" title={oc.description || ""}>{oc.code}</span>
        <div className="ta-oc-bar-wrap">
          <div className="ta-oc-bar" style={{ width: `${oc.pct ?? 0}%`, background: pctColor(oc.pct) }} />
        </div>
        <PctBadge value={oc.pct} />
      </div>
      {hasLinks && (
        <div className="ta-oc-links">
          {/* YouTube video kartları */}
          {videos.map((v, i) => {
            const yt = ytMap[v.search_query];
            const url = yt?.video_url
              || `https://www.youtube.com/results?search_query=${encodeURIComponent(v.search_query || v.title || "")}`;
            const title = yt?.title || v.title || "YouTube";
            const thumb = yt?.thumbnail;
            const dur = yt?.duration_sec
              ? `${Math.round(yt.duration_sec / 60)} dk`
              : v.duration_min ? `${v.duration_min} dk` : null;

            return (
              <a key={`yt-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                 className="ta-mini-video-card">
                <div className="ta-mini-thumb">
                  {ytLoading
                    ? <div className="ta-mini-thumb-loading" />
                    : thumb
                      ? <img src={thumb} alt="" loading="lazy" />
                      : <span className="ta-mini-play">▶</span>
                  }
                </div>
                <div className="ta-mini-info">
                  <span className="ta-mini-title" title={title}>
                    {title.slice(0, 52)}{title.length > 52 ? "…" : ""}
                  </span>
                  <span className="ta-mini-meta">
                    {yt?.channel || v.channel || "YouTube"}
                    {dur && <> · {dur}</>}
                  </span>
                </div>
              </a>
            );
          })}

          {/* Web kaynak mini kartları */}
          {webRes.map((r, i) => (
            <a key={`web-${i}`}
               href={buildWebUrl(r.site_key, r.title || r.description || "")}
               target="_blank" rel="noopener noreferrer"
               className="ta-mini-web-card"
               title={r.title || ""}>
              <div className="ta-mini-web-icon">{typeIcon(r.type)}</div>
              <div className="ta-mini-info">
                <span className="ta-mini-title">
                  {(r.title || SITE_LABELS[r.site_key] || r.site_key || "Web").slice(0, 40)}
                </span>
                <span className="ta-mini-meta ta-mini-meta-web">
                  {SITE_LABELS[r.site_key] || r.site_key}
                  {r.type && <> · {r.type}</>}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── StudentRow: tıklanabilir öğrenci satırı + detay ─── */
function StudentRow({ student, aiResult, isExpanded, onToggle }) {
  const overallKey = "overall_post_midterm_pct";
  const ocList = student.learning_outcomes || [];
  // AI sonuçlarını outcome_code'a göre indeksle
  const aiMap = {};
  (aiResult?.analyses || []).forEach(a => { aiMap[a.outcome_code] = a; });

  return (
    <div className={`ta-student-row ${isExpanded ? "ta-student-expanded" : ""}`}>
      <button className="ta-student-header" onClick={onToggle}>
        <span className="ta-sno">{student.student_no}</span>
        <span className="ta-sname">{student.student_name || "—"}</span>
        <PctBadge value={student[overallKey]} />
        <span className="ta-sc-counts">
          ÖÇ: {ocList.length}
          {(() => {
            const groups = {};
            ocList.forEach(o => {
              const cat = categoryFromPercent(o.pct ?? 0);
              groups[cat.key] = groups[cat.key] || { label: cat.label, count: 0 };
              groups[cat.key].count++;
            });
            const clsMap = { 0: "ta-sc-weak", 1: "ta-sc-weak", 2: "ta-sc-ok", 3: "ta-sc-ok-teal", 4: "ta-sc-ok-green" };
            return Object.values(groups)
              .sort((a, b) => b.key - a.key)
              .map(g => (
                <span key={g.label} className={clsMap[g.key]}> · {g.count} {g.label}</span>
              ));
          })()}
        </span>
        <span className="ta-s-arrow">{isExpanded ? "▲" : "▼"}</span>
      </button>

      {isExpanded && (
        <div className="ta-student-detail">
          {ocList.length === 0 ? (
            <p className="ta-no-data">Bu öğrenci için ÖÇ verisi yok.</p>
          ) : (
            <>
              <p className="ta-detail-sub">Öğrenme Çıktıları Performansı</p>
              <div className="ta-oc-list">
                {ocList.map(oc => (
                  <OutcomeRow
                    key={oc.id || oc.code}
                    oc={oc}
                    analysis={aiMap[oc.code] || null}
                  />
                ))}
              </div>
              {aiResult?.error && (
                <div className="ta-ai-partial-error">
                  AI kaynakları yüklenemedi: {aiResult.error}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── PhaseStep ─── */
function PhaseStep({ num, label, status, sub }) {
  // status: idle | active | done
  return (
    <div className={`ta-phase-step ta-phase-${status}`}>
      <div className="ta-phase-dot">
        {status === "done" ? "✓" : status === "active" ? <span className="ta-spinner" /> : num}
      </div>
      <div className="ta-phase-text">
        <span className="ta-phase-label">{label}</span>
        {sub && <span className="ta-phase-sub">{sub}</span>}
      </div>
    </div>
  );
}

/* ─── Main component ─── */
export default function TopluAnaliz() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseName, setCourseName] = useState("");

  // phase: 0=idle 1=loading-data 2=analyzing 3=done
  const [phase, setPhase] = useState(0);
  const [classData, setClassData] = useState(null);
  const [phase1Summary, setPhase1Summary] = useState(null); // {ocCount, pcCount, studentCount}
  const [currentIdx, setCurrentIdx] = useState(0);
  const [analysisResults, setAnalysisResults] = useState({}); // {student_no: {analyses:[]} | {error:str}}
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [loadError, setLoadError] = useState("");

  const isRunning = useRef(false);
  const abortRef = useRef(false);

  // Course list
  useEffect(() => {
    const aid = sessionStorage.getItem("akademisyen_id");
    if (!aid) return;
    fetch(`${API_BASE_URL}/api/akademisyen/courses?akademisyen_id=${aid}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setCourses(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const handleCourseChange = (id) => {
    setCourseId(id);
    const c = courses.find(x => String(x.id) === String(id));
    setCourseName(c ? `${c.code} — ${c.name}` : "");
    setPhase(0);
    setClassData(null);
    setPhase1Summary(null);
    setAnalysisResults({});
    setExpandedStudent(null);
    setLoadError("");
    setCurrentIdx(0);
    isRunning.current = false;
    abortRef.current = false;
  };

  const analyzeStudent = useCallback(async (student) => {
    const weakOc = (student.learning_outcomes || []).filter(o => (o.pct ?? 999) < 65);
    if (weakOc.length === 0) return { analyses: [] };

    const res = await fetch(`${API_BASE_URL}/api/ai/student-outcome-resources`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        course_id: courseId,
        course_name: courseName,
        outcomes: weakOc.map(o => ({
          code: o.code, description: o.description, pct: o.pct,
          exam_evidence: o.exam_evidence || [],
        })),
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "AI hatası");
    return { analyses: data.analyses || [] };
  }, [courseId, courseName]);

  const runAnalysis = useCallback(async (students, existingResults) => {
    isRunning.current = true;
    abortRef.current = false;

    for (let i = 0; i < students.length; i++) {
      if (abortRef.current) break;
      const s = students[i];
      const key = String(s.student_no);
      if (existingResults[key]) continue; // resume: already done

      setCurrentIdx(i);

      let result = null;
      // 2 deneme
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          result = await analyzeStudent(s);
          break;
        } catch (e) {
          if (attempt === 1) {
            result = { error: e.message, analyses: [] };
          }
        }
      }

      setAnalysisResults(prev => ({ ...prev, [key]: result }));
    }

    if (!abortRef.current) setPhase(3);
    isRunning.current = false;
  }, [analyzeStudent]);

  const handleStart = useCallback(async () => {
    if (!courseId || isRunning.current) return;
    if (phase === 3 && classData) {
      // Resume from failed/unfinished
      runAnalysis(classData.students || [], analysisResults);
      setPhase(2);
      return;
    }

    setPhase(1);
    setLoadError("");
    setAnalysisResults({});
    setCurrentIdx(0);

    let data;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/pre-midterm/post-midterm-class-breakdown?course_id=${courseId}`,
        { credentials: "include" }
      );
      data = await res.json();
      if (data.error) throw new Error(data.error);
    } catch (e) {
      setLoadError(`Veri yüklenemedi: ${e.message}`);
      setPhase(0);
      return;
    }

    setClassData(data);
    const students = data.students || [];

    // Compute summary
    const allOc = new Set();
    const allPc = new Set();
    students.forEach(s => {
      (s.learning_outcomes || []).forEach(o => allOc.add(o.code));
      (s.program_outcomes || []).forEach(o => allPc.add(o.code));
    });
    setPhase1Summary({ ocCount: allOc.size, pcCount: allPc.size, studentCount: students.length });

    setPhase(2);
    runAnalysis(students, {});
  }, [courseId, phase, classData, analysisResults, runAnalysis]);

  /* derived */
  const students = classData?.students || [];
  const doneCount = Object.keys(analysisResults).length;
  const progress = students.length > 0 ? Math.round((doneCount / students.length) * 100) : 0;
  const currentStudent = students[currentIdx];

  const phase1Status = phase === 0 ? "idle" : phase === 1 ? "active" : "done";
  const phase2Status = phase < 2 ? "idle" : phase === 2 ? "active" : "done";
  const phase3Status = phase < 3 ? "idle" : "done";

  const phase1Sub = phase1Summary
    ? `${phase1Summary.studentCount} öğrenci · ${phase1Summary.ocCount} ÖÇ · ${phase1Summary.pcCount} PÖÇ yüklendi`
    : phase === 1 ? "Veriler yükleniyor…" : null;

  const phase2Sub = phase === 2
    ? `${doneCount}/${students.length} öğrenci — ${currentStudent?.student_name || ""}`
    : phase > 2 ? `${doneCount} öğrenci tamamlandı` : null;

  const canStart = courseId && phase !== 1 && phase !== 2;
  const canResume = phase === 3 && students.some(s => analysisResults[String(s.student_no)]?.error);

  return (
    <div className="ta-wrap">
      <h2 className="ta-title">Kişiselleştirilmiş Analiz</h2>

      {/* Course selector */}
      <div className="ta-controls">
        <label className="ta-label">
          Ders:
          <SearchableDropdown
            value={courseId}
            onChange={handleCourseChange}
            disabled={phase === 1 || phase === 2}
            placeholder="— Ders seçin —"
            searchPlaceholder="Ders adı veya koduyla ara…"
            ariaLabel="Ders"
            options={courses.map(c => ({
              value: c.id,
              label: `${c.code} — ${c.name}`,
              searchText: `${c.code || ''} ${c.name || ''}`,
            }))}
          />
        </label>

        <button
          className={`ta-start-btn ${canStart ? "" : "ta-start-disabled"}`}
          disabled={!canStart}
          onClick={handleStart}
        >
          {phase === 0 ? "Tüm Öğrencileri Analiz Et"
            : phase === 3 && canResume ? "Hatalıları Yeniden İşle"
            : phase === 3 ? "Yeniden Analiz Et"
            : "İşleniyor…"}
        </button>
      </div>

      {loadError && <div className="ta-error">{loadError}</div>}

      {/* Phase indicator */}
      {phase > 0 && (
        <div className="ta-phases">
          <PhaseStep num="1" label="Kademe 1 — Veri Yükleme" status={phase1Status} sub={phase1Sub} />
          <div className="ta-phase-connector" />
          <PhaseStep num="2" label="Kademe 2 — AI Analizi" status={phase2Status} sub={phase2Sub} />
          <div className="ta-phase-connector" />
          <PhaseStep num="3" label="Kademe 3 — Tamamlandı" status={phase3Status}
            sub={phase3Status === "done" ? `${doneCount} öğrenci işlendi` : null} />
        </div>
      )}

      {/* Progress bar (phase 2) */}
      {phase === 2 && (
        <div className="ta-progress-wrap">
          <div className="ta-progress-bar">
            <div className="ta-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="ta-progress-label">{progress}%</span>
        </div>
      )}

      {/* Student list (phase 2 + 3) */}
      {phase >= 2 && students.length > 0 && (
        <div className="ta-student-list">
          {students.map((s, i) => {
            const key = String(s.student_no);
            const aiResult = analysisResults[key];
            const status = aiResult
              ? aiResult.error ? "error" : "done"
              : i === currentIdx && phase === 2 ? "active"
              : phase === 2 && i > currentIdx ? "waiting"
              : "waiting";

            return (
              <div key={key} className="ta-student-wrap">
                {/* Status indicator strip */}
                <div className={`ta-status-strip ta-strip-${status}`} title={
                  status === "done" ? "Tamamlandı"
                    : status === "active" ? "İşleniyor…"
                    : status === "error" ? "Hata — tekrar denenecek"
                    : "Bekliyor"
                } />
                <StudentRow
                  student={s}
                  aiResult={aiResult || null}
                  isExpanded={expandedStudent === key}
                  onToggle={() => setExpandedStudent(prev => prev === key ? null : key)}
                />
              </div>
            );
          })}
        </div>
      )}

      {phase === 3 && doneCount > 0 && (
        <div className="ta-done-banner">
          Analiz tamamlandı! {doneCount} öğrenci işlendi.
          Öğrenciye tıklayarak ÖÇ detaylarını ve kaynakları görüntüleyin.
        </div>
      )}
    </div>
  );
}
